import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import LinkTableCellComponent from '../common/LinkTableCellComponent';
import Label1 from '../common/Label1';
import ContractStepper, {STEPPER_STATE} from './ContractStepper';
import ActionButton3 from '../common/Action Buttons/ActionButton3';
import IconActionButton from '../common/IconActionButton';
import CustomSidePanel from '../common/CustomSidePanel';
import {roles} from '../../utils/auth';
import ContractStatus from '../common/ContractStatus';
import {CONTRACT_STATUS} from '../../utils/contracts';
import {currencyFormatter, formatDate} from '../../utils/formatter';
import {getDurationInMonth} from '../../utils';
import {getCustomerDocument} from '../../api/network/businessCustomerService';
import {downloadDocument} from '../../api/network/documentService';
import {ReactComponent as HandShake} from "../../assets/HandShake.svg";
import {ReactComponent as Delivery} from "../../assets/Delivery.svg";
import {ReactComponent as Lock} from "../../assets/Lock.svg";
import {ReactComponent as Wallet} from "../../assets/Wallet.svg";

const PageWrapper = styled.div`
  max-width: 590px ;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  max-height: 100vh;
  flex: 1;
  overflow-y: scroll;
  padding: 40px 44px 100px 44px;
`;

const ContractInfo = styled.div`
    display: grid;
    grid-auto-flow: row;
    grid-template-columns: repeat(3, max-content);
    grid-template-rows: 1fr 1fr;
    grid-column-gap: 80px;
    grid-row-gap: 48px;
    
    margin: 30px 0;
    
    > div {
        display: grid;
        grid-template-rows: 1fr 1fr;
        grid-row-gap: 10px;    
    }
`;

const ContractStepperWrapper = styled.div`
    margin: 40px 0;
`;

const ContractHeading = styled.div`
    display: grid;
    margin-bottom: 30px;
    grid-template-rows: 1fr 1fr;
    grid-row-gap: 24px;
    
    font-style: normal;
    font-family: Metropolis;
    
    .heading {
        font-weight: 500;
        font-size: 24px;
        line-height: 24px;
    }
    
    .sub-heading {
        font-weight: normal;
        font-size: 18px;
        line-height: 18px;
        display: flex;
        flex-direction: row;
         > * {
            margin-left: 10px;
         }
    } 
`;

const CustomDivider = styled.hr`
    border: 1px dashed var(--color-gray-400);
    width: 100%;
`;

const ActionsWrapper = styled.div`
    position: absolute;
    bottom: 0;
    width: 100%;
    height: fit-content;
    
    margin-bottom: 40px;
    
    display: flex;
    flex-direction: row;
    justify-content: start;
    
     > * {
      margin-right: 24px;
    }
    
    padding: 0 40px;
`;

function ContractInfoSidePanel(props) {
    const {contract = {}, showPanel, onViewContractClick, onSignContractClick, onReadyForDeliveryClick, onPanelClose, role} = props;

    const {contractNumber, status, startDateOfContract, endDateOFContract, monthlyPayment, totalCashPrice, dateSigned, deliveryEstimate, lastUpdated, companyName} = contract;

    const showReadyForDelivery = (role === roles.SMS);

    const deliveryActionButton = (role, status) => {
        switch (role) {
            case roles.SMS:
                if (status === CONTRACT_STATUS.AGREEMENT_SIGNED)
                    return <IconActionButton onClick={onReadyForDeliveryClick}>Send Invoice</IconActionButton>;
                break;
            case roles.GK:
                if (status === CONTRACT_STATUS.DELIVERED)
                    return <IconActionButton onClick={onReadyForDeliveryClick}>Payment Issued</IconActionButton>;
                break;
            case roles.CUSTOMER_REP:
                if (status === CONTRACT_STATUS.DELIVERY_SLIP_SENT)
                    return <IconActionButton onClick={onReadyForDeliveryClick}>Upload Delivery Slip</IconActionButton>;
                break;
            default:
                return null;
        }

        return null;
    };

    const onDownloadContractClick = () => {
        getCustomerDocument(contract.companyId, contract.customerId, contract.documentId)
            .then(result => {
                downloadDocument(result.documentId, (error, result) => {
                    console.log(error, result);
                });
            }).catch(error => console.log('error', error));
    };

    const getContractSteps = contract => {
        const currentStatus = contract?.status;

        const steps = [
            {
                icon: <HandShake/>,
                contractStatuses: [

                ],
                state: STEPPER_STATE.IN_ACTIVE,
                label: 'Contract Signed'
            },
            {
                icon: <Delivery/>,
                contractStatuses: [
                    CONTRACT_STATUS.AGREEMENT_SIGNED,
                    CONTRACT_STATUS.DELIVERY_SLIP_SENT,
                    CONTRACT_STATUS.DELIVERY_SLIP_SIGNED,
                ],
                state: STEPPER_STATE.IN_ACTIVE,
                label: 'Device Delivery'
            },
            {
                icon: <Lock/>,
                contractStatuses: [
                    CONTRACT_STATUS.DELIVERED
                ],
                state: STEPPER_STATE.IN_ACTIVE,
                label: 'Policies Added'
            },
            {
                icon: <Wallet/>,
                contractStatuses: [
                    CONTRACT_STATUS.PAYMENT_CONFIRMED,
                    CONTRACT_STATUS.GK_PAID,
                    CONTRACT_STATUS.CONTRACT_STARTED
                ],
                state: STEPPER_STATE.IN_ACTIVE,
                label: 'Contract Payments'
            }
        ];

        let isActive;
        // eslint-disable-next-line no-restricted-syntax
        for (let i = steps.length - 1; i >= 0; i--) {
            // eslint-disable-next-line no-prototype-builtins,no-continue
            if (!steps.hasOwnProperty(i)) continue;

            if (isActive) {
                steps[i].state = STEPPER_STATE.COMPLETED;
            } else if (steps[i].contractStatuses.includes(currentStatus) || i === 0) {
                steps[i].state = STEPPER_STATE.ACTIVE;
                isActive = true;
            } else {
                steps[i].state = STEPPER_STATE.IN_ACTIVE;
            }
        }

        return steps;
    };

    return (
        <PageWrapper>
            <CustomSidePanel open={showPanel} onDrawerClose={onPanelClose}>
                <ContentWrapper>
                    <ContractHeading>
                        <div className="heading">
                            Contract ID: <span style={{color: 'var(--color-blue-600)'}}>{contractNumber}</span>
                        </div>
                        <div className="sub-heading">
                            <span>Status: </span>
                            <ContractStatus status={status} role={role}/></div>
                    </ContractHeading>

                    <CustomDivider/>

                    <ContractInfo>
                        <div>
                            <Label1>
                                Plan Duration
                            </Label1>

                            <div>
                                {getDurationInMonth(startDateOfContract, endDateOFContract)}
                            </div>
                        </div>

                        <div>
                            <Label1>
                                Monthly Payment
                            </Label1>

                            <div>
                                ${currencyFormatter(monthlyPayment)}
                            </div>
                        </div>

                        <div>
                            <Label1>
                                Total Cash Price
                            </Label1>

                            <div>
                                ${currencyFormatter(totalCashPrice)}
                            </div>
                        </div>

                        {/*----*/}

                        <div>
                            <Label1>
                                Date Signed
                            </Label1>

                            <div>
                                {formatDate(dateSigned)}
                            </div>
                        </div>

                        <div>
                            <Label1>
                                Delivery Estimate
                            </Label1>

                            <div>
                                {formatDate(deliveryEstimate)}
                            </div>
                        </div>

                        <div>
                            <Label1>
                                Last Updated
                            </Label1>

                            <div>
                                {formatDate(lastUpdated)}
                            </div>
                        </div>
                    </ContractInfo>

                    <CustomDivider/>

                    <ContractStepperWrapper>
                        <ContractStepper status={contract.status} steps={getContractSteps(contract)}/>
                    </ContractStepperWrapper>

                </ContentWrapper>

                <ActionsWrapper>
                    {/*<ActionButton3 onClick={onViewContractClick} style={{marginRight: "24px"}}>View</ActionButton3>*/}
                    <ActionButton3
                        onClick={onDownloadContractClick}
                        style={{marginRight: '24px'}}
                    >Download</ActionButton3>
                    {!contract.status === CONTRACT_STATUS.AGREEMENT_SIGNED &&
                    <ActionButton3 onClick={onSignContractClick}>Sign</ActionButton3>}
                    {
                        deliveryActionButton(role, status)
                    }
                </ActionsWrapper>
            </CustomSidePanel>
        </PageWrapper>
    );
}

ContractInfoSidePanel.propTypes = {contract: PropTypes.array.isRequired};
ContractInfoSidePanel.defaultProps = {contract: {}};

export default ContractInfoSidePanel;
